import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '@medflex/components/stories/Modal';
import { useTranslation } from 'react-i18next';
import Button, { KINDS as BUTTON_KINDS } from '@medflex/components/stories/Button';
import Link from '@medflex/components/stories/Link';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import useLocationSearchParams from '@medflex/hooks/useLocationSearchParams';
import { useHistory } from 'react-router-dom';
import OptionCardGroup from '@medflex/components/stories/OptionCardGroup/OptionCardGroup';
import config from '@medflex/utils/config';
import Spinner from '@medflex/components/stories/Spinner';
import ScrollableTabs from '@medflex/components/stories/ScrollableTabs';
import InputTextarea from '@medflex/components/stories/InputTextarea';
import useModal from '../hooks/useModal';
import { MODALS } from '../hooks/useIsModal';
import useQuestionnaires from '../hooks/useQuestionnaires';
import useMessages from '../hooks/useMessages';
import useChatContacts from '../hooks/useChatContacts';
import useExternalServices, { EXTERNAL_SERVICES } from '../hooks/useExternalServices';
import useSessionChat from '../hooks/useSessionChat';

const TAB_TYPES = {
  GROUPS: 'groups',
  DOCUMENTS: 'documents',
};

const INPUT_NAMES = {
  [TAB_TYPES.GROUPS]: 'questionnaire-groups',
  [TAB_TYPES.DOCUMENTS]: 'questionnaire-documents',
};

const STEPS = {
  DOCUMENTS: 'documents',
  MESSAGE: 'message',
};

const NewQuestionnaireModal = () => {
  const { t } = useTranslation('physician', { keyPrefix: 'NewQuestionnaireModal' });
  const { userId, tab, sessionId, ...urlParams } = useLocationSearchParams();
  const { close } = useModal();
  const {
    isLoading,
    questionnaires,
    load: loadQuestionnaires,
    createSession,
  } = useQuestionnaires();
  const { send } = useMessages();
  const { send: sendSessionChatMessage } = useSessionChat({
    id: sessionId,
  });
  const { contacts } = useChatContacts();
  const history = useHistory();
  const [simpleprax] = useExternalServices([EXTERNAL_SERVICES.SIMPLEPRAX]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [sessionCreationPending, setSessionCreationPending] = useState(false);
  const [step, setStep] = useState(STEPS.DOCUMENTS);
  const [message, setMessage] = useState(t('messageDefault'));

  const options = useMemo(() => {
    const { groups, documents } = questionnaires;
    if (tab === TAB_TYPES.GROUPS) {
      return groups.map(({ title, id }) => ({
        value: id,
        label: title,
      }));
    }

    return documents.map(({ title, id }) => ({
      value: id,
      label: title,
    }));
  }, [questionnaires, tab]);

  useEffect(() => {
    setStep(urlParams.step || STEPS.DOCUMENTS);
  }, [urlParams.step]);

  useEffect(() => {
    if (urlParams.documents?.length > 0) {
      const documents = Array.isArray(urlParams.documents)
        ? urlParams.documents
        : [urlParams.documents];
      const selected = documents
        .map(id => options.find(option => option.value === id))
        .filter(Boolean);

      setSelectedOptions(selected);
    }
  }, [urlParams.documents, options]);

  useEffect(() => {
    loadQuestionnaires();
  }, [loadQuestionnaires]);

  const handleClose = e => {
    e.preventDefault();
    close({
      modal: MODALS.NEW_QUESTIONNAIRE,
      userId,
      tab,
      step: null,
      documents: null,
    });
  };

  const createQuestionnaireSession = async ({ tab, documentIds, message }) => {
    setSessionCreationPending(true);
    const { data } = await createSession({
      userId,
      ...(tab === TAB_TYPES.GROUPS ? { documentGroupId: documentIds[0] } : { documentIds }),
    });
    if (data?.attributes?.url) {
      const { id: chatId, user } = contacts.find(({ user }) => user.id === userId);

      const urlParts = data.attributes.url.split('#');

      const questionnaireUrl = new URL(urlParts[0]);
      questionnaireUrl.searchParams.append('firstname', user.firstName);
      questionnaireUrl.searchParams.append('lastname', user.lastName);
      questionnaireUrl.searchParams.append('birthdate', user.dateOfBirth);

      const text = `${message}\n\n${questionnaireUrl}#${urlParts[1]}`;

      if (sessionId) {
        await sendSessionChatMessage({
          text,
        });

        close({
          modal: MODALS.NEW_QUESTIONNAIRE,
          userId,
          tab,
          step: null,
          documents: null,
          sessionId,
        });
      } else {
        await send({
          recipient: userId,
          text,
        });
        history.push(`/chat/${chatId}`);
      }
    }
    setSessionCreationPending(false);
  };

  const handleSelect = useCallback(
    options => {
      if (!options) return;
      setSelectedOptions(options);
    },
    [setSelectedOptions],
  );

  const tabs = [
    {
      key: TAB_TYPES.GROUPS,
      label: t('tabs.documentGroups'),
    },
    {
      key: TAB_TYPES.DOCUMENTS,
      label: t('tabs.documents'),
    },
  ];

  const onTabClick = tab => {
    setSelectedOptions([]);
    const searchParams = new URLSearchParams({
      modal: 'newQuestionnaire',
      userId,
      tab,
    });

    if (sessionId) {
      searchParams.append('sessionId', sessionId);
    }

    history.replace({
      search: searchParams.toString(),
    });
  };

  const handleSubmitDocuments = async e => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const documents = formData.getAll(INPUT_NAMES[tab]);

    const urlSearchParams = new URLSearchParams({
      modal: 'newQuestionnaire',
      userId,
      tab,
      step: STEPS.MESSAGE,
    });

    if (sessionId) {
      urlSearchParams.append('sessionId', sessionId);
    }

    documents.forEach(id => urlSearchParams.append('documents', id));

    history.push({
      search: urlSearchParams.toString(),
    });
    setStep(STEPS.MESSAGE);
  };

  const handleSubmitMessage = e => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const message = formData.get('message');
    const documentIds = formData.getAll('documentIds');

    createQuestionnaireSession({ tab, documentIds, message });
  };

  const handleResetMessage = e => {
    e.preventDefault();
    setMessage(t('messageDefault'));
  };

  return (
    <Modal showCloseButton onClickCloseButton={handleClose}>
      <div className="text-xl font-semibold mb-8">{t('title')} </div>

      {isLoading || sessionCreationPending ? (
        <div className="flex flex-col justify-center items-center gap-2">
          <Spinner size="xl" />
          <p className="text-center text-slate-700">{isLoading ? t('loading') : t('creating')}</p>
        </div>
      ) : (
        <>
          {step === STEPS.DOCUMENTS && (
            <div className="flex flex-col gap-2">
              <div className="mb-2 w-full border-gray-300 border-b-1">
                <ScrollableTabs active={tab} onClick={onTabClick} tabs={tabs} />
              </div>
              <form className="flex flex-col gap-2" onSubmit={handleSubmitDocuments}>
                <div className="h-96 overflow-y-auto overflow-x-hidden">
                  <OptionCardGroup
                    key={tab}
                    options={options}
                    groupName={`questionnaire-${tab}`}
                    onChange={handleSelect}
                    type={tab === TAB_TYPES.GROUPS ? 'radio' : 'checkbox'}
                  />
                </div>
                <Link
                  href={`${config.SIMPLEPRAX_APP_URL}/${simpleprax?.organizationId}/documents?tab=${tab}`}
                  className="space-x-1"
                  target="_blank"
                >
                  <PlusCircleIcon className="h-5 w-5" />
                  <span>
                    {tab === TAB_TYPES.GROUPS ? t('createNew.group') : t('createNew.document')}
                  </span>
                </Link>

                <div className="flex justify-end">
                  <Button
                    kind={BUTTON_KINDS.PRIMARY}
                    type="submit"
                    isDisabled={selectedOptions.length === 0}
                  >
                    {t('next')}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {step === STEPS.MESSAGE && (
            <form
              onSubmit={handleSubmitMessage}
              onReset={handleResetMessage}
              className="flex flex-col gap-8"
            >
              {selectedOptions.map(({ value }) => (
                <input
                  type="checkbox"
                  className="hidden"
                  name="documentIds"
                  value={value}
                  key={value}
                  defaultChecked
                />
              ))}
              <div className="flex flex-col gap-2">
                <label className="flex flex-col gap-2">
                  <span className="font-semibold">{t('messageLabel')}</span>
                  <InputTextarea name="message" value={message} onChange={setMessage} />
                </label>
                <Button type="reset" kind={BUTTON_KINDS.LINK} className="self-end">
                  {t('reset')}
                </Button>
              </div>
              <div className="flex justify-between">
                <Button kind={BUTTON_KINDS.PRIMARY_OUTLINE} onClick={() => history.goBack()}>
                  {t('back')}
                </Button>
                <Button kind={BUTTON_KINDS.PRIMARY} type="submit" isDisabled={message.length === 0}>
                  {t('submit')}
                </Button>
              </div>
            </form>
          )}
        </>
      )}
    </Modal>
  );
};

export default NewQuestionnaireModal;
